import React from "react";

/**
 * Certificate Extensions Display Component
 * Displays all parsed X.509 v3 certificate extensions
 * Styled to match the existing Technical Details section
 */
// Module scope, not inside CertificateExtensions : a component created
// during render is a new component type on every render, so React unmounted
// and remounted every box each time the parent re-rendered
// (react-hooks/static-components).
const DataBox = ({ label, children, className = "" }) => (
  <div
    className={`bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px] ${className}`}
  >
    <span className="text-xs font-medium text-muted uppercase tracking-wider">
      {label}
    </span>
    <div className="text-heading mt-1 font-mono">{children}</div>
  </div>
);

const CertificateExtensions = ({ extensions }) => {
  if (!extensions || Object.keys(extensions).length === 0) {
    return null;
  }

  // Helper to render a simple data box matching existing style.
  //
  // THE LABEL CARRIES text-xs ; THE VALUE CARRIES NO SIZE AT ALL. Both
  // halves are copied from the Technical Details rows ChainAnalyzer
  // renders as this card's siblings (search that file for
  // `uppercase tracking-wider`), so the two sections read as one design
  // rather than two. Do not size the children : an unsized value
  // inherits the base prose size from here, which is the whole point.
  //
  // The nested sub-labels (OCSP, CA Issuers, Permitted, Excluded) carry
  // an explicit font-sans because they are the only labels that sit
  // INSIDE the value wrapper, and so inherited its font-mono - the same
  // label role rendering in a different typeface from the label above
  // it. The wrapper cannot drop font-mono : the values are what need it.
  //
  // Until 2026-08-24 the children overrode it anyway, and disagreed with
  // each other - fourteen at text-sm and eight at text-xs, the smaller
  // ones being whichever values happened to be long (hex identifiers,
  // URLs, log IDs). That is per-element taste, which the Design
  // Constitution in docs/UI_STANDARD.md forbids by name ; long values
  // are handled by `break-all`, not by shrinking the text.
  return (
    <div className="card p-6">
      {/* h2, not h3 : this component renders its OWN `card p-6` as a
          direct sibling of the Chain Analyzer's other result cards, all
          of which are h2. Left at h3 it read as a subsection of
          whichever card happened to precede it - Subject Alternative
          Names when that block renders, Technical Details when it does
          not. The 2026-08-23 heading promotion swept src/client/pages
          and this heading does not live there, which is the same miss
          the comment below already records. */}
      <h2 className="text-lg font-semibold text-heading mb-2">
        SSL Certificate Extensions
      </h2>

      {/* Every heading carries a sentence saying what the section is
          for (CLAUDE.md, "Consistent page structure"). This one had
          none, and the operator found it after I reported the page
          complete - I had checked the page file and not the components
          it renders. */}
      <p className="text-body mb-4">
        Extensions are the instructions a Certificate Authority (CA) writes into
        an SSL Certificate, and they decide what it may be used for. They record
        which host names it covers, whether it may sign other SSL Certificates,
        and where a browser can check whether it has been revoked. A browser
        reads them on every connection, so a host name absent here is refused
        even though the SSL Certificate is valid.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Key Usage */}
        {extensions.keyUsage && extensions.keyUsage.length > 0 && (
          <DataBox label="Key Usage">
            <p>{extensions.keyUsage.join(", ")}</p>
          </DataBox>
        )}

        {/* Extended Key Usage */}
        {extensions.extKeyUsage && extensions.extKeyUsage.length > 0 && (
          <DataBox label="Extended Key Usage">
            {extensions.extKeyUsage.map((usage, index) => (
              <div key={index}>{usage.name}</div>
            ))}
          </DataBox>
        )}

        {/* Basic Constraints */}
        {extensions.basicConstraints && (
          <DataBox label="Basic Constraints">
            <p>
              CA : {extensions.basicConstraints.isCA ? "TRUE" : "FALSE"}
              {extensions.basicConstraints.pathLenConstraint !== undefined &&
                ` (Path Length: ${extensions.basicConstraints.pathLenConstraint})`}
            </p>
          </DataBox>
        )}

        {/* Certificate Policies */}
        {extensions.certificatePolicies &&
          extensions.certificatePolicies.length > 0 && (
            <DataBox label="Certificate Policies">
              {extensions.certificatePolicies.map((policy, index) => (
                <div key={index}>
                  {policy.name} ({policy.oid})
                </div>
              ))}
            </DataBox>
          )}

        {/* Subject Key Identifier */}
        {extensions.subjectKeyIdentifier && (
          <DataBox label="Subject Key Identifier">
            <p className="break-all">{extensions.subjectKeyIdentifier}</p>
          </DataBox>
        )}

        {/* Authority Key Identifier */}
        {extensions.authorityKeyIdentifier && (
          <DataBox label="Authority Key Identifier">
            <p className="break-all">
              {extensions.authorityKeyIdentifier.keyIdentifier}
            </p>
          </DataBox>
        )}

        {/* Authority Information Access */}
        {extensions.authorityInfoAccess && (
          <DataBox label="Authority Information Access">
            {extensions.authorityInfoAccess.ocsp &&
              extensions.authorityInfoAccess.ocsp.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-sans font-medium text-muted uppercase tracking-wider mb-1">
                    OCSP
                  </div>
                  {extensions.authorityInfoAccess.ocsp.map((url, index) => (
                    <a
                      key={index}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-link hover:text-link-hover break-all"
                    >
                      {url}
                    </a>
                  ))}
                </div>
              )}
            {extensions.authorityInfoAccess.caIssuers &&
              extensions.authorityInfoAccess.caIssuers.length > 0 && (
                <div>
                  <div className="text-xs font-sans font-medium text-muted uppercase tracking-wider mb-1">
                    CA Issuers
                  </div>
                  {extensions.authorityInfoAccess.caIssuers.map(
                    (url, index) => (
                      <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-link hover:text-link-hover break-all"
                      >
                        {url}
                      </a>
                    )
                  )}
                </div>
              )}
          </DataBox>
        )}

        {/* CRL Distribution Points */}
        {extensions.crlDistributionPoints &&
          extensions.crlDistributionPoints.length > 0 && (
            <DataBox label="CRL Distribution Points">
              {extensions.crlDistributionPoints.map((url, index) => (
                <a
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-link hover:text-link-hover break-all"
                >
                  {url}
                </a>
              ))}
            </DataBox>
          )}

        {/* Certificate Transparency */}
        {extensions.certificateTransparency &&
          extensions.certificateTransparency.length > 0 && (
            <DataBox label="Certificate Transparency (SCTs)">
              <div>
                {extensions.certificateTransparency.length} Signed Certificate
                Timestamp
                {extensions.certificateTransparency.length > 1 ? "s" : ""}
              </div>
              {extensions.certificateTransparency.map((sct, index) => (
                <div
                  key={index}
                  className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 first:border-t-0 first:mt-0 first:pt-0"
                >
                  <div className="text-body">
                    SCT {index + 1} ({sct.version})
                  </div>
                  <div className="text-body break-all mt-1">
                    Log ID: {sct.logId.substring(0, 50)}...
                  </div>
                  <div className="text-body mt-1">
                    Timestamp: {new Date(sct.timestamp).toLocaleString()}
                  </div>
                </div>
              ))}
            </DataBox>
          )}

        {/* Name Constraints */}
        {extensions.nameConstraints && (
          <DataBox label="Name Constraints">
            {extensions.nameConstraints.permitted &&
              extensions.nameConstraints.permitted.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-sans font-medium text-muted uppercase tracking-wider mb-1">
                    Permitted
                  </div>
                  {extensions.nameConstraints.permitted.map((name, index) => (
                    <div key={index}>
                      {name.type}: {name.value}
                    </div>
                  ))}
                </div>
              )}
            {extensions.nameConstraints.excluded &&
              extensions.nameConstraints.excluded.length > 0 && (
                <div>
                  <div className="text-xs font-sans font-medium text-muted uppercase tracking-wider mb-1">
                    Excluded
                  </div>
                  {extensions.nameConstraints.excluded.map((name, index) => (
                    <div key={index}>
                      {name.type}: {name.value}
                    </div>
                  ))}
                </div>
              )}
          </DataBox>
        )}

        {/* Policy Constraints */}
        {extensions.policyConstraints && (
          <DataBox label="Policy Constraints">
            {extensions.policyConstraints.requireExplicitPolicy !==
              undefined && (
              <div>
                Require Explicit Policy :{" "}
                {extensions.policyConstraints.requireExplicitPolicy}
              </div>
            )}
            {extensions.policyConstraints.inhibitPolicyMapping !==
              undefined && (
              <div>
                Inhibit Policy Mapping :{" "}
                {extensions.policyConstraints.inhibitPolicyMapping}
              </div>
            )}
          </DataBox>
        )}

        {/* Delegated Credentials */}
        {extensions.delegatedCredentials && (
          <DataBox label="Delegated Credentials">
            <p>Supported (RFC 9345)</p>
            <p className="text-body">{extensions.delegatedCredentials.oid}</p>
          </DataBox>
        )}

        {/* Unknown Extensions */}
        {extensions.unknown && extensions.unknown.length > 0 && (
          <DataBox label="Other Extensions">
            {extensions.unknown.map((ext, index) => (
              <div
                key={index}
                className="mb-2 pb-2 border-b border-gray-200 dark:border-gray-700 last:border-b-0 last:mb-0 last:pb-0"
              >
                <div>{ext.oid}</div>
                {ext.critical && (
                  <div className="text-red-600 dark:text-red-300">CRITICAL</div>
                )}
                <div className="text-body break-all mt-1">
                  {ext.value.substring(0, 100)}
                  {ext.value.length > 100 ? "..." : ""}
                </div>
              </div>
            ))}
          </DataBox>
        )}
      </div>
    </div>
  );
};

export default CertificateExtensions;
